import PropTypes from 'prop-types';
import { Heading, LinkButton, Notice, Text, textVariants, Tooltip, vars } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { ChargingCurrency, PriceFormat } from '@eti/types';
import { constants, price as priceUtils } from '@eti/utils';
import { screenReadersOnlyStyles } from '../../../utils/styleUtils.ts';
import { CartRow, Price } from './CartComponents';
import CartPaymentFees from './CartPaymentFees';
import PriceInEuro from './PriceInEuro';
import DisplayChargingCurrency from './DisplayChargingCurrency';

const totalPriceRowStyles = css`
  align-items: center;
`;

const noAdditionalFeesStyles = css`
  padding: 4px 0;
`;

const titleStyles = (isPaymentBottomContent) => css`
  ${!isPaymentBottomContent && `font-size: 18px`};
  padding-bottom: 10px;
`;

const discountStyles = css`
  color: ${vars.colors.signal.positive.foreground};
`;

const CartPriceSummary = ({
  chargeCurrencyPriceFormat,
  dataTestId,
  discountAmount,
  instalmentFee,
  isOtherChargeCurrency,
  isPaymentBottomContent,
  paymentMethods = [],
  potentialChargeInDifferentCurrencyText,
  priceInChargeCurrency,
  selectedPaymentMethod,
  shouldShowPaymentOptions = true,
  shouldAddInstallmentFeeInTotalAmount,
  subTotalPrice,
  totalPrice,
  chargingCurrency,
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { createPriceFormatter, formatPrice } = priceUtils;
  const discountCode = sessionStorage.getItem('discountCode') || '';
  const totalPriceWithFees =
    instalmentFee && shouldAddInstallmentFeeInTotalAmount ? totalPrice + instalmentFee : totalPrice;
  const priceFormatter = isOtherChargeCurrency && createPriceFormatter(chargeCurrencyPriceFormat);
  const paymentMethodsContainFees = paymentMethods.some((method) => method.price > 0);
  const shouldShowNoAdditionalPaymentFeeLabel =
    p('IbeClient.NoPaymentFee.Enabled') &&
    !paymentMethodsContainFees &&
    (selectedPaymentMethod === undefined || selectedPaymentMethod.price === 0) &&
    (discountAmount === undefined || discountAmount === 0);
  const showPaymentFees = paymentMethods.length > 0 && shouldShowPaymentOptions;
  const subtotalFormattedPrice = subTotalPrice > 0 && formatPrice(subTotalPrice);
  const serviceFeePrice =
    Boolean(selectedPaymentMethod?.serviceFee) && formatPrice(selectedPaymentMethod.serviceFee);
  const selectedPaymentMethodPrice =
    Boolean(selectedPaymentMethod?.price) && formatPrice(selectedPaymentMethod?.price);
  const instalmentFeePrice = instalmentFee && formatPrice(instalmentFee);
  const discountAmountPrice = Boolean(discountAmount) && formatPrice(-Math.abs(discountAmount));
  const totalPriceWithFeesFormatted = totalPrice !== null && formatPrice(totalPriceWithFees);

  return (
    <div data-testid={dataTestId}>
      <Heading
        className={titleStyles(isPaymentBottomContent)}
        level={isPaymentBottomContent ? 2 : 3}
      >
        {t('Cart.PriceSummary.Header.Label')}
      </Heading>
      {subTotalPrice > 0 && (
        <CartRow
          aria-label={`${t(
            'Cart.PriceSummary.Subtotal.Label',
          )} ${priceUtils.replaceWithCurrencyName(subtotalFormattedPrice)}`}
        >
          <Text aria-hidden>{t('Cart.PriceSummary.Subtotal.Label')}</Text>
          <Price aria-hidden data-testid={`${dataTestId}-subtotalPrice`}>
            {subtotalFormattedPrice}
          </Price>
        </CartRow>
      )}
      {Boolean(selectedPaymentMethod?.serviceFee) && (
        <CartRow
          aria-label={`${t('Cart.ServiceFee.Text')} ${priceUtils.replaceWithCurrencyName(
            serviceFeePrice,
          )}`}
        >
          <Text data-testid={`${dataTestId}-paymentMethodServiceFee-label`}>
            {t('Cart.ServiceFee.Text')}
          </Text>
          <Price
            aria-hidden
            data-testid={`${dataTestId}-paymentMethodServiceFee`}
            isDiscount={Boolean(selectedPaymentMethod.serviceFee < 0)}
            size="large"
          >
            {serviceFeePrice}
          </Price>
        </CartRow>
      )}
      {Boolean(selectedPaymentMethod?.price) && (
        <>
          <Text className={screenReadersOnlyStyles} readOnly>
            {t(
              'ScreenReader.PriceSpecification.DiscountAnnouncement.AriaLabel',
              selectedPaymentMethod.label,
              priceUtils.replaceWithCurrencyName(selectedPaymentMethodPrice),
            )}
          </Text>
          <CartRow aria-hidden>
            <Text data-testid={`${dataTestId}-paymentMethodPrice-label`}>
              {selectedPaymentMethod.label}
            </Text>
            <Price
              data-testid={`${dataTestId}-paymentMethodPrice`}
              isDiscount={Boolean(selectedPaymentMethod.price < 0)}
              size="large"
            >
              {selectedPaymentMethodPrice}
            </Price>
          </CartRow>
        </>
      )}
      {instalmentFee && (
        <CartRow
          aria-label={`${t(
            'Payment.CardInstalments.PaymentPlan.InstalmentFee.Label',
          )} ${priceUtils.replaceWithCurrencyName(instalmentFeePrice)}`}
        >
          <Text data-testid={`${dataTestId}-paymentInstalmentsFee-label`}>
            {t('Payment.CardInstalments.PaymentPlan.InstalmentFee.Label')}
          </Text>
          <Price aria-hidden data-testid={`${dataTestId}-instalmentFee`} size="large">
            {instalmentFeePrice}
          </Price>
        </CartRow>
      )}
      {Boolean(discountAmount) && (
        <CartRow
          aria-label={`${t('Payment.Cart.Discount.Label')}${
            discountCode && ` ”${discountCode}”`
          } ${priceUtils.replaceWithCurrencyName(discountAmountPrice)}`}
          className={constants.MOUSE_FLOW_CLASS_NAMES}
          data-testid={`${dataTestId}-discount`}
        >
          <Text aria-hidden className={discountStyles}>
            {`${t('Payment.Cart.Discount.Label')}${discountCode && ` ”${discountCode}”`}`}
          </Text>
          <Price
            aria-hidden
            data-testid={`${dataTestId}-discountAmount`}
            isDiscount={Boolean(discountAmount !== 0)}
            size="large"
          >
            {discountAmountPrice}
          </Price>
        </CartRow>
      )}
      {shouldShowNoAdditionalPaymentFeeLabel && (
        <Notice
          className={noAdditionalFeesStyles}
          data-testid={`${dataTestId}-no-additional-payment-fee`}
        >
          <div>{t('Cart.NoAdditionalPaymentFees.Label')}</div>
        </Notice>
      )}
      {showPaymentFees && (
        <CartRow>
          <Tooltip
            content={<CartPaymentFees paymentMethods={paymentMethods} />}
            role="dialog"
            title={t('Cart.PaymentFees.Label')}
            trigger="click"
          >
            <LinkButton data-testid={`${dataTestId}-fees-tooltip-link`}>
              {t('Cart.PaymentFees.Label')}
            </LinkButton>
          </Tooltip>
        </CartRow>
      )}
      {totalPrice !== null && (
        <CartRow
          aria-label={`${t('Cart.PriceSummary.Total.Label')} ${priceUtils.replaceWithCurrencyName(
            totalPriceWithFeesFormatted,
          )}`}
          className={totalPriceRowStyles}
        >
          <Text
            aria-hidden
            as="p"
            data-testid={`${dataTestId}-total-pay`}
            variant={textVariants._16_BOLD}
          >
            {t('Cart.PriceSummary.Total.Label')}
          </Text>
          <Text
            aria-hidden
            as="p"
            className="notranslate"
            data-testid={`${dataTestId}-totalPrice`}
            variant={textVariants._16_BOLD}
          >
            {totalPriceWithFeesFormatted}
          </Text>
        </CartRow>
      )}
      {p('IbeClient.DualCroatianCurrencyDisplay.Enabled') && (
        <PriceInEuro dataTestId={dataTestId} totalPriceWithFees={totalPriceWithFees} />
      )}

      {p('TravelerDetails.ChargingCurrency.Enabled') && chargingCurrency && (
        <DisplayChargingCurrency
          chargingCurrency={chargingCurrency}
          totalPriceWithFees={totalPriceWithFees}
        />
      )}

      {isOtherChargeCurrency && (
        <CartRow
          aria-label={`${t(
            'PaymentPage.Confirm.OtherChargeCurrency',
          )} ${priceUtils.replaceWithCurrencyName(
            priceFormatter.formatPrice(priceInChargeCurrency),
          )}`}
          className={totalPriceRowStyles}
          data-testid={`${dataTestId}-other-charge-currency`}
        >
          <Text>{t('PaymentPage.Confirm.OtherChargeCurrency')}</Text>
          <Price aria-hidden className="notranslate">
            &nbsp;
            {priceFormatter.formatPrice(priceInChargeCurrency)}
          </Price>
        </CartRow>
      )}
      {potentialChargeInDifferentCurrencyText && (
        <CartRow>{potentialChargeInDifferentCurrencyText}</CartRow>
      )}
    </div>
  );
};

CartPriceSummary.propTypes = {
  chargeCurrencyPriceFormat: PriceFormat,
  chargingCurrency: ChargingCurrency,
  dataTestId: PropTypes.string,
  discountAmount: PropTypes.number,
  instalmentFee: PropTypes.number,
  isOtherChargeCurrency: PropTypes.bool,
  isPaymentBottomContent: PropTypes.bool,
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      price: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  potentialChargeInDifferentCurrencyText: PropTypes.string,
  priceInChargeCurrency: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({
    label: PropTypes.string,
    price: PropTypes.number,
    serviceFee: PropTypes.number,
  }),
  shouldAddInstallmentFeeInTotalAmount: PropTypes.bool,
  shouldShowPaymentOptions: PropTypes.bool,
  subTotalPrice: PropTypes.number,
  totalPrice: PropTypes.number,
};

export default CartPriceSummary;
